/*
 * This illustrates how to create a widget that makes use of a number of different field definition types.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    heading = "heading",
    content = "content",
    textFieldValue3 = "textFieldValue3",
    imageLink = "imageLink",
    destLink = "destLink",
    openInNewWindow = "openInNewWindow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.content]: string;
        [fields.textFieldValue3]: string;
        [fields.imageLink]: string;
        [fields.destLink]: string;
        [fields.openInNewWindow]: boolean;
    };
}

type Props = OwnProps;

export interface CustomBlogTileStyles {
    container?: InjectableCss;
    link?: LinkPresentationProps;
    imageWrapper?: InjectableCss;
    sectionHeadig?: InjectableCss;
    sectionContent?: InjectableCss;
}

const CustomBlogTileStyles: CustomBlogTileStyles = {
    container: {
        css: css`
            margin: 30px 0 30px;
            text-align: left;
            font-size: 14px;
            padding: 30px 15px;
            min-height: 220px;
            height: 100%;
            border: 1px solid #e4e4ed;
            box-shadow: 0 2px 4px #e4e4ed;
            background: #fff;
        `,
    },
    link: {
        typographyProps: {
            color: "text.main",
        },
    },
    imageWrapper: {
        css: css`
            text-align: center;
            width: 100%;
            height: 100%;
            > img {
                width: 100%;
            },
        `,
    },
    sectionHeadig: {
        css: css`
            font-size: 18px;
            line-height: 24px;
            margin-bottom: 10px;
            margin-top: 10px;
        `,
    },
};

const CustomBlogTile: React.FC<Props> = ({ id, fields }) => {
    const { destLink, imageLink, heading, content, textFieldValue3, openInNewWindow } = fields;
    return (
        <StyledWrapper {...CustomBlogTileStyles.container}>
            {destLink ? (
                <Link href={destLink || "{null}"} target={openInNewWindow ? "_blank" : ""}>
                    <StyledWrapper {...CustomBlogTileStyles.imageWrapper}>
                        <img src={imageLink} alt="" />
                    </StyledWrapper>
                    <StyledWrapper {...CustomBlogTileStyles.sectionHeadig}>{heading}</StyledWrapper>
                    <StyledWrapper>{content}</StyledWrapper>
                    <StyledWrapper>{textFieldValue3}</StyledWrapper>
                    <Zone contentId={id} zoneName="Content" requireRows />
                </Link>
            ) : (
                <StyledWrapper>
                    <StyledWrapper {...CustomBlogTileStyles.imageWrapper}>
                        <img src={imageLink} alt="" />
                    </StyledWrapper>
                    <StyledWrapper {...CustomBlogTileStyles.sectionHeadig}>{heading}</StyledWrapper>
                    <StyledWrapper>{content}</StyledWrapper>
                    <StyledWrapper>{textFieldValue3}</StyledWrapper>
                    <Zone contentId={id} zoneName="Content" requireRows />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            name: fields.heading,
            displayName: "Heading",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "text placeholder",
        },
        {
            name: fields.content,
            displayName: "ContentLine1",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "text placeholder",
        },
        {
            name: fields.textFieldValue3,
            displayName: "ContentLine2",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "text placeholder",
        },
        {
            name: fields.imageLink,
            displayName: "Image Link",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "text placeholder",
        },
        {
            name: fields.destLink,
            displayName: "Destination Link",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "text placeholder",
        },
        {
            name: fields.openInNewWindow,
            displayName: "Open in new window",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: CustomBlogTile,
    definition,
};

export default widgetModule;
